/** Multi-line text input with label, description, and error. Wraps React Aria TextField + TextArea. */
import {
  TextField as AriaTextField,
  TextArea as AriaTextArea,
  Label,
  Text,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cn } from '@/foundation/utils/cn';

interface TextAreaProps extends AriaTextFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string;
  className?: string;
  textAreaClassName?: string;
  placeholder?: string;
  rows?: number;
  /** Strip all chrome (border, focus ring, padding). Useful for embedding in custom containers. */
  bare?: boolean;
}

export function TextArea({
  label,
  description,
  errorMessage,
  className,
  textAreaClassName,
  placeholder,
  rows = 3,
  bare = false,
  ...props
}: TextAreaProps) {
  return (
    <AriaTextField className={cn('flex flex-col gap-1', className)} {...props}>
      {label && (
        <Label className="block text-sm font-medium text-[var(--f-fg-secondary)]">{label}</Label>
      )}
      <AriaTextArea
        className={cn(
          'w-full text-[var(--f-fg)] transition-all',
          bare ? 'bg-transparent' : 'bg-[var(--f-bg-raised)]',
          bare
            ? 'resize-none border-none p-0 focus:outline-none focus:ring-0'
            : [
                'border border-[var(--f-border-strong)] rounded-xl p-2',
                'focus:outline-none focus:ring-2 focus:ring-[var(--f-accent)] focus:border-transparent',
              ],
          textAreaClassName
        )}
        placeholder={placeholder}
        rows={rows}
      />
      {description && (
        <Text slot="description" className="text-xs text-[var(--f-fg-muted)]">
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text slot="errorMessage" className="text-xs text-[var(--f-error-text)]">
          {errorMessage}
        </Text>
      )}
    </AriaTextField>
  );
}
